import React, { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DocsVerificationStepEnum, VerificationPhotosKeysEnum } from '@models/enums';
import type { ThemeProps } from '@models/types';
import { NoMoreAttemptsStep } from '@modules/dashboard/pages/docs-verification/docs-verification-page/docs-verification-steps/no-more-attempts';
import type { DocsVerificationStepsProps } from '@modules/dashboard/pages/docs-verification/docs-verification-page/docs-verification-steps/types';
import { useSelectedChecks } from '@modules/dashboard/pages/docs-verification/docs-verification-page/hooks/use-selected-checks';
import useTheme from '@mui/material/styles/useTheme';
import type { RootState } from '@store/root-reducer';
import { docsVerificationSlice } from '@store/slices/docs-verification/docs-verification-slice';
import * as faceDetection from '@tensorflow-models/face-detection';

import { VerificationStep } from '../tabs';

import { FACE_DETECTOR_CONFIG } from './constants';
import { DesktopNotAllowedStep } from './dekstop-not-allowed';
import { ExpiredStep } from './expired';
import { FinishStep } from './finish';
import { LandscapeError } from './landscape-error';
import { StartStep } from './start';

export const DocsVerificationSteps: React.FC<DocsVerificationStepsProps> = ({ onFinish }) => {
    const dispatch = useDispatch();
    const theme = useTheme<ThemeProps>();
    const checks = useSelector((state: RootState) => state.docsVerification.sdkOptions?.checks);
    const currentStep = useSelector((state: RootState) => state.docsVerification.currentStep);
    const faceDetector = useSelector((state: RootState) => state.docsVerification.faceDetector);
    const isLivenessCheck = useMemo(
        () => checks?.includes(VerificationPhotosKeysEnum.LIVENESS_PHOTO),
        [checks],
    );
    const isInitialized = useRef(false);
    const { isHasLiveness } = useSelectedChecks();

    useEffect(() => {
        if (!isHasLiveness && isLivenessCheck && !faceDetector && !isInitialized.current) {
            isInitialized.current = true;
            const initialize = async () => {
                const detector = await faceDetection.createDetector(
                    faceDetection.SupportedModels.MediaPipeFaceDetector,
                    FACE_DETECTOR_CONFIG,
                );

                dispatch(docsVerificationSlice.actions.setFaceDetector(detector));
            };

            initialize();
        }
    }, [isHasLiveness, isLivenessCheck, dispatch, faceDetector]);

    return (
        <React.Fragment>
            {theme.isLandscape && <LandscapeError />}
            {currentStep === DocsVerificationStepEnum.EXPIRED && <ExpiredStep />}
            {currentStep === DocsVerificationStepEnum.DESKTOP_NOT_ALLOWED && (
                <DesktopNotAllowedStep />
            )}
            {currentStep === DocsVerificationStepEnum.START && <StartStep />}
            {currentStep === DocsVerificationStepEnum.VERIFICATION && <VerificationStep />}
            {currentStep === DocsVerificationStepEnum.FINISH && <FinishStep onFinish={onFinish} />}
            {currentStep === DocsVerificationStepEnum.NO_MORE_ATTEMPTS && <NoMoreAttemptsStep />}
        </React.Fragment>
    );
};
